import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './MachineList.css';
import '../table-list.css';
import iconMachine from '../assets/svg/icon-machine.svg';
import iconMachineDark from '../assets/svg/icon-machine-dark.svg';
import { ReactComponent as ContextMenuIcon } from '../assets/svg/icon-more-options.svg';
import { ReactComponent as ToggleRowIcon } from '../assets/svg/icon-dropdown-arrow.svg';
import { ReactComponent as HeaderIcon } from '../assets/svg/header-icon-machines.svg';
import '../topmenu.css'; // Import top menu styling
import { useTranslation } from 'react-i18next';
import { ApiBaseUrlContext } from '../App';
import { ReactComponent as FilterIcon } from '../assets/svg/icon-filter.svg';
import machineHolder from '../assets/picture/machineholder.jpg';

const MachineList = () => {
    const [machines, setMachines] = useState([]);
    const [groupedMachines, setGroupedMachines] = useState({});
    const [totalMachines, setTotalMachines] = useState(0);
    const [totalMaintenanceCost, setTotalMaintenanceCost] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [currencySymbol, setCurrencySymbol] = useState('kr');
    const [currencyPosition, setCurrencyPosition] = useState('right');
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [hoveredMachine, setHoveredMachine] = useState(null);
    const [selectedMachineCost, setSelectedMachineCost] = useState(null);
    const [isCostModalOpen, setIsCostModalOpen] = useState(false);
    const [sortBy, setSortBy] = useState('machine'); 
    const [showFilters, setShowFilters] = useState(false);
    const { t } = useTranslation();
    const [openMenuId, setOpenMenuId] = useState(null);
    const [showContent] = useState(true);
    const navigate = useNavigate();
    

    const apiBaseUrl = useContext(ApiBaseUrlContext);

    const filterMachines = (machines, query) => {
        if (!query) return machines; // If search query is empty, return all machines
        return machines.filter(machine =>
            machine.machine.toLowerCase().includes(query.toLowerCase()) ||
            (machine.dncmaxgroupname && machine.dncmaxgroupname.toLowerCase().includes(query.toLowerCase())) ||
            (machine.category && machine.category.toLowerCase().includes(query.toLowerCase()))
        );
    };

    useEffect(() => {
        axios.get('/mnt/api/machines/all?includeAll=true')
            .then(async (response) => {
                const machinesData = response.data;
    
                // Fetch cost details for each machine
                const machineCostPromises = machinesData.map(async (machine) => {
                    try {
                        const costResponse = await axios.get(`/mnt/api/machines/cost/${machine.machineid}`);
                        return { 
                            ...machine, 
                            image_path: machine.image_path || machineHolder, // Apply fallback image
                            machineTotalCost: costResponse.data.machineTotalCost,
                            serviceReportTimeCost: costResponse.data.serviceReportTimeCost || 0,
                            serviceReportMaterialCost: costResponse.data.serviceReportMaterialCost || 0,
                            serviceReportTotalCost: costResponse.data.serviceReportTotalCost || 0,
                            maintenanceTimeCost: costResponse.data.maintenanceTimeCost || 0,
                            maintenanceMaterialCost: costResponse.data.maintenanceMaterialCost || 0,
                            maintenanceTotalCost: costResponse.data.maintenanceTotalCost || 0 
                        };
                    } catch (err) {
                        return { 
                            ...machine, 
                            image_path: machineHolder, // Apply fallback image in case of error
                            machineTotalCost: 0 
                        };
                    }
                });
    
                const updatedMachines = await Promise.all(machineCostPromises);
                setMachines(updatedMachines);
                setTotalMachines(updatedMachines.length);
                setTotalMaintenanceCost(updatedMachines.reduce((sum, machine) => sum + machine.machineTotalCost, 0));
    
                // Group Machines by Group Name
                const grouped = updatedMachines.reduce((acc, machine) => {
                    if (!acc[machine.dncmaxgroupname]) {
                        acc[machine.dncmaxgroupname] = [];
                    }
                    acc[machine.dncmaxgroupname].push(machine);
                    return acc;
                }, {});
                setGroupedMachines(grouped);
            })
            .catch(err => console.error('Error fetching machines:', err));
    }, []);

    useEffect(() => {
        const fetchSettings = async () => {
          try {
            const response = await axios.get(`${apiBaseUrl}/mnt/api/settings`);
            const settings = response.data;
      
            setCurrencySymbol(settings.currencySymbol || 'kr');
            setCurrencyPosition(settings.currencyPosition || 'right');
          } catch (error) {
            console.error('Error fetching settings:', error);
          }
        };
      
        fetchSettings();
      }, [apiBaseUrl]);

    // Handle Cost Breakdown Modal
    const handleShowCostDetails = (e, machine) => {
        e.stopPropagation();
        setSelectedMachineCost(machine);
        setIsCostModalOpen(true);
    };

    // Function to toggle menu
    const toggleMenu = (event, machineId) => {
        event.stopPropagation(); // Prevent table row click from closing immediately
        setOpenMenuId(openMenuId === machineId ? null : machineId);
    };

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest(".menu-container")) {
                setOpenMenuId(null);
            }
        };

        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    const getPriorityClass = (priority) => {
        switch (priority?.toLowerCase()) {
            case 'high':
                return 'box-item--red'; // Define styles in CSS
            case 'medium':
                return 'box-item--yellow';
            case 'low':
                return 'box-item--green';
            default:
                return 'box-item--grey';
        }
    };

    return (
        <>
            {/* Filter Section */}
            {showFilters && (
                <div className="filter-container" onClick={() => setShowFilters(!showFilters)}>
                    <div className="filter" onClick={(e) => e.stopPropagation()}>
                        <div className="filter__fields">
                            <div className="entry-item">
                                <input 
                                    type="text"
                                    placeholder={t('machineList.searchPlaceholder')}
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="entry-control"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
    
            <div className="content">

                <div className="top-menu">
                    <div className="top-menu__divider"></div>
                    <div className="top-menu__icon-button top-menu__filter-button" onClick={() => setShowFilters(!showFilters)}>
                        <FilterIcon />
                    </div>
                </div>

                {/* Header */}
                <div className="content__main-title">
                    <div className="content__main-title-icon"><HeaderIcon /></div>
                    {t("machineList.machines")}
                </div>
                
                {/*
                <div className="machine-list-header">
                    <img src={isDarkMode ? iconMachineDark : iconMachine} alt="Machines" className="machine-icon" />
                    <h1>{t("machineList.machines")}</h1>
                </div>
                */}

                {/* Summary Section */}
                <div class="header-info-container">
                    <div class="header-info-container__title">Details</div>
                    <div class="header-info-container__inner">
                        <table>
                            <tbody>
                                <tr><td>{t("machineList.numberOfMachines")}</td><td>{totalMachines}</td></tr>
                                <tr><td>{t("machineList.totalMaintenanceCost")}</td><td>
                                {currencyPosition === 'left'
                                ? ` ${currencySymbol}${totalMaintenanceCost.toFixed(2)}`
                                : ` ${totalMaintenanceCost.toFixed(2)} ${currencySymbol}`
                                }
                                </td></tr>
                            </tbody>
                        </table>    
                    </div>
                </div>

                
                <div>
                    <table class="table-list">
                        <tbody>                    



                            {Object.entries(groupedMachines).map(([groupName, machines], index) => {

                                const filteredMachines = filterMachines(machines, searchQuery);
                                if (filteredMachines.length === 0) return null;

                                const showHeader = index === 0;

                                return ( 

                                    <>

                                        <tr class="table-list__section">
                                            <td colSpan={window.innerWidth < 700 ? 2 : 8}>{groupName}</td>
                                        </tr>
                                        {showHeader && (
                                        <tr class="table-list__header-row">
                                            <td class="table-list__header-cell-first">{t("machineList.machine")}</td>
                                            <td></td>
                                            <td class="table-list__hide-mobile">{t("machineList.priority")}</td>
                                            <td class="table-list__hide-mobile">{t("machineList.location")}</td>
                                            <td class="table-list__hide-mobile">{t("machineList.category")}</td>
                                            <td class="table-list__hide-mobile">{t("machineList.control")}</td>
                                            <td class="table-list__hide-mobile table-list__align-right">{t("machineList.mntCost")}</td>
                                            <td class="table-list__hide-mobile"></td> 
                                        </tr>
                                        )}

                                        {filteredMachines.map(machine => (

                                            <>

                                                <tr className="table-list__machine-row"
                                                    key={machine.machineid}
                                                    onClick={(e) => {
                                                        if (window.innerWidth < 700) {
                                                            const currentRow = e.currentTarget;
                                                            const nextRow = currentRow.nextElementSibling;
                                                            if (nextRow) {
                                                                const isVisible = nextRow.style.display === 'table-row';
                                                                nextRow.style.display = isVisible ? 'none' : 'table-row';

                                                                // Toggle class on current <tr>
                                                                currentRow.classList.toggle('table-list__selected-mobile', !isVisible);
                                                            }
                                                        } else {
                                                            navigate(`/machine/${machine.machineid}`);
                                                        }
                                                    }}
                                                >
                                                <td className="table-list__expand-row">
                                                    <div class="table-list__toggle-icon"><ToggleRowIcon /></div>
                                                    <a href="#" className="table-list__primary-btn">{t("machineList.view")}</a>
                                                    {machine.machine}
                                                </td>
                                                <td class="table-list__thumb-container">
                                                    <div class="table-list__big-thumb-container">
                                                        
                                                        {/* Image big thumbnail */}
                                                        {machine.image_path && (
                                                            <img 
                                                                src={machine.image_path} 
                                                                alt={machine.machine} 
                                                                onClick={() => navigate(`/machine/${machine.machineid}`)}
                                                                onError={(e) => e.target.src = machineHolder} // Handle broken images
                                                            />
                                                        )}

                                                    </div>                  
                                                    <div class="table-list__small-thumb-container">

                                                        {/* Image small thumbnail */}
                                                        {machine.image_path && (
                                                            <img 
                                                                src={machine.image_path} 
                                                                alt={machine.machine} 
                                                                onError={(e) => e.target.src = machineHolder} // Handle broken images
                                                            />
                                                        )}
                                                        
                                                    </div>
                                                </td>
                                                <td className={`table-list__hide-mobile table-list__dot-color table-list__dot-color-${machine.priority.toLowerCase()}`}>{machine.priority}</td>
                                                <td class="table-list__hide-mobile">{machine.dncmaxgroupname || "-"}</td>
                                                <td class="table-list__hide-mobile">{machine.category || "-"}</td>
                                                <td class="table-list__hide-mobile">{machine.system || "-"}</td>
                                                <td className="table-list__hide-mobile table-list__align-right" onClick={(e) => handleShowCostDetails(e, machine)}>
                                                    {currencyPosition === 'left'
                                                        ? ` ${currencySymbol}${machine.machineTotalCost.toFixed(2)}`
                                                        : ` ${machine.machineTotalCost.toFixed(2)} ${currencySymbol}`
                                                    }
                                                </td>
                                                <td class="table-list__hide-mobile table-list__options table-list__align-right" onClick={(e) => toggleMenu(e, machine.machineid)}>
                                                    
                                                    <div class="table-list__options-icon"><ContextMenuIcon /></div>

                                                    {openMenuId === machine.machineid && (
                                                    <div class="table-list__context-menu">
                                                        <Link to={`/machine/${machine.machineid}`} className="table-list__context-menu-option">{t("machineList.viewMachine")}</Link>
                                                    </div> 
                                                    )}
                                                    
                                                </td>
                                            </tr>
                                            <tr class="table-list__mobile-data-container">
                                                <td colSpan={window.innerWidth < 700 ? 2 : 8}>
                                                    
                                                    <table class="table-list__mobile-data-table">
                                                        <tr><td>{t("machineList.priority")}</td><td>Low</td></tr>

                                                        <tr><td>{t("machineList.location")}</td><td>{machine.dncmaxgroupname || "-"}</td></tr>
                                                        <tr><td>{t("machineList.category")}</td><td>{machine.category || "-"}</td></tr>
                                                        <tr><td>{t("machineList.control")}</td><td>{machine.system || "-"}</td></tr>
                                                        <tr><td>{t("machineList.mntCost")}</td><td>
                                                            {currencyPosition === 'left'
                                                                ? ` ${currencySymbol}${machine.machineTotalCost.toFixed(2)}`
                                                                : ` ${machine.machineTotalCost.toFixed(2)} ${currencySymbol}`
                                                            }
                                                        </td></tr>
                                                    </table>

                                                    <div class="table-list__mobile-data-container-options">
                                                        
                                                        <button type="button" className="button--blue" onClick={() => navigate(`/machine/${machine.machineid}`)}>{t("machineList.view")}</button>
                                                        
                                                        <button type="button" className="slim-button more-options" onClick={(e) => toggleMenu(e, machine.machineid)}><ContextMenuIcon /></button>
                                                        
                                                        {openMenuId === machine.machineid && (
                                                        <div class="table-list__context-menu">
                                                            <Link to={`/machine/${machine.machineid}`} className="table-list__context-menu-option">{t("machineList.viewMachine")}</Link>
                                                        </div> 
                                                        )}    
                                                    </div>

                                                </td>
                                            </tr>                



                                            </>

                                        ))}

                                    </>

                                );
                            })}
                            
                        </tbody>
                    </table>
                </div>


    
                {/* Fix: Move Modal Inside return() */}
                {isCostModalOpen && selectedMachineCost && (
                    <div className="modal-overlay" onClick={() => setIsCostModalOpen(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2>{t("costBreakdown.title")}</h2>
                        <p><strong>{t("costBreakdown.serviceReportTimeCost")}:</strong> 
                        {currencyPosition === 'left' 
                            ? ` ${currencySymbol} ${selectedMachineCost.serviceReportTimeCost.toFixed(2)}`
                            : ` ${selectedMachineCost.serviceReportTimeCost.toFixed(2)} ${currencySymbol}`}
                        </p>
                        <p><strong>{t("costBreakdown.serviceReportMaterialCost")}:</strong> 
                        {currencyPosition === 'left' 
                            ? ` ${currencySymbol} ${selectedMachineCost.serviceReportMaterialCost.toFixed(2)}`
                            : ` ${selectedMachineCost.serviceReportMaterialCost.toFixed(2)} ${currencySymbol}`}
                        </p>
                        <p><strong>{t("costBreakdown.serviceReportTotalCost")}:</strong> 
                        {currencyPosition === 'left' 
                            ? ` ${currencySymbol} ${selectedMachineCost.serviceReportTotalCost.toFixed(2)}`
                            : ` ${selectedMachineCost.serviceReportTotalCost.toFixed(2)} ${currencySymbol}`}
                        </p>
                        <hr />
                        <p><strong>{t("costBreakdown.maintenanceTimeCost")}:</strong> 
                        {currencyPosition === 'left' 
                            ? ` ${currencySymbol} ${selectedMachineCost.maintenanceTimeCost.toFixed(2)}`
                            : ` ${selectedMachineCost.maintenanceTimeCost.toFixed(2)} ${currencySymbol}`}
                        </p>
                        <p><strong>{t("costBreakdown.maintenanceMaterialCost")}:</strong> 
                        {currencyPosition === 'left' 
                            ? ` ${currencySymbol} ${selectedMachineCost.maintenanceMaterialCost.toFixed(2)}`
                            : ` ${selectedMachineCost.maintenanceMaterialCost.toFixed(2)} ${currencySymbol}`}
                        </p>
                        <p><strong>{t("costBreakdown.maintenanceTotalCost")}:</strong> 
                        {currencyPosition === 'left' 
                            ? ` ${currencySymbol} ${selectedMachineCost.maintenanceTotalCost.toFixed(2)}`
                            : ` ${selectedMachineCost.maintenanceTotalCost.toFixed(2)} ${currencySymbol}`}
                        </p>
                        <hr />
                        <p><strong>{t("costBreakdown.totalMachineCost")}:</strong> 
                        {currencyPosition === 'left' 
                            ? ` ${currencySymbol} ${selectedMachineCost.machineTotalCost.toFixed(2)}`
                            : ` ${selectedMachineCost.machineTotalCost.toFixed(2)} ${currencySymbol}`}
                        </p>
                        <hr />
                        <button className="button--grey" onClick={() => setIsCostModalOpen(false)}>
                        {t("costBreakdown.closeButton")}
                        </button>
                    </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default MachineList;
